import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Users, CheckCircle, ArrowRight } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { navigateToStartHere } from '../utils/navigation';

export function MarketingPage() {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <>
      <Helmet>
        <title>Social Media Marketing - Priority AI Services</title>
        <meta
          name="description"
          content="Done-for-you social media for busy clinic owners. Look active, trustworthy, and easy to book on Facebook—without logging in after a 10-hour day."
        />
      </Helmet>

      <div className="min-h-screen pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero */}
          <div className="text-center mb-16">
            <Users className="w-16 h-16 text-blue-500 mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Done-for-you social media for busy clinic owners.
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-8">
              You look after patients. I make sure your clinic looks active, trustworthy, and easy to book on Facebook and online—without you having to log in after a 10-hour day.
            </p>
            <button
              onClick={() => navigateToStartHere(navigate, location.pathname)}
              className="inline-flex items-center gap-2 px-8 py-4 bg-blue-500 text-white text-lg font-semibold rounded-md hover:bg-blue-600 transition-colors"
            >
              Start Here
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>

          {/* Who this is for */}
          <div className="bg-gray-900/50 backdrop-blur-sm border border-blue-500/20 rounded-lg p-8 mb-12">
            <h2 className="text-3xl font-bold text-white mb-6">Who this is for</h2>
            <p className="text-gray-300 mb-4 leading-relaxed">
              This is for osteopathy, physio, massage, chiropractic, med spa, and similar clinic owners who:
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-gray-300">
                <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <span>Are in the treatment room most of the day.</span>
              </li>
              <li className="flex items-start gap-3 text-gray-300">
                <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <span>Have a weak or inconsistent Facebook/Instagram presence.</span>
              </li>
              <li className="flex items-start gap-3 text-gray-300">
                <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <span>Rely mostly on word-of-mouth.</span>
              </li>
              <li className="flex items-start gap-3 text-gray-300">
                <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <span>Know they're leaving money on the table by not showing up consistently online.</span>
              </li>
            </ul>
          </div>

          {/* Why social media feels like a job */}
          <div className="bg-black/40 border border-blue-500/20 rounded-lg p-8 mb-12">
            <h2 className="text-3xl font-bold text-white mb-6">Why social media feels like a job you don't have time for</h2>
            <div className="space-y-4 text-gray-300">
              <p className="leading-relaxed">
                <strong className="text-white">Posting always gets pushed to "tomorrow".</strong> You finish a full day of patients, and the last thing you want to do is think about captions, hashtags, and what to post.
              </p>
              <p className="leading-relaxed">
                <strong className="text-white">Inactive pages quietly push potential patients to competitors.</strong> When someone looks you up and sees your last post was from 6 months ago, they assume you're not taking new clients—or worse, that you're not trustworthy.
              </p>
              <p className="leading-relaxed">
                <strong className="text-white">You don't need viral content—just consistent, trustworthy presence.</strong> Most clinic owners think they need to become content creators. You don't. You just need to show up consistently so when someone searches for help, they find you first.
              </p>
            </div>
          </div>

          {/* What I actually do */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">What I actually do for you</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-900/50 backdrop-blur-sm border border-blue-500/20 rounded-lg p-6">
                <h3 className="text-xl font-bold text-white mb-4">Profile cleanup & optimization</h3>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <span>Clean up Facebook/IG profiles so they look professional and match what people search for.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <span>Add clear CTAs, contact info, and booking options.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <span>Make sure your hours, services, and location are easy to find.</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-900/50 backdrop-blur-sm border border-blue-500/20 rounded-lg p-6">
                <h3 className="text-xl font-bold text-white mb-4">Done-for-you content</h3>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <span>Consistent posting (3-5x/week) of educational, trust-building content.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <span>Simple graphics and captions that match your clinic's voice.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <span>No salesy garbage—just helpful content that positions you as the local expert.</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-900/50 backdrop-blur-sm border border-blue-500/20 rounded-lg p-6">
                <h3 className="text-xl font-bold text-white mb-4">Light local promotion</h3>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <span>Simple offers matched to your ideal clients (e.g., free consults, intro packages).</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <span>Posts designed to get engagement and shares in your local area.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <span>Clear calls-to-book that make it easy for people to take action.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <span>Optional light Facebook ad management to promote your key services locally.</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-900/50 backdrop-blur-sm border border-blue-500/20 rounded-lg p-6">
                <h3 className="text-xl font-bold text-white mb-4">Facebook ad management (when you're ready)</h3>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <span>Simple, local Facebook campaigns that promote your best services.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <span>Clear offers that drive people to book—no complicated funnels.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <span>I keep the ad strategy aligned with your clinic's goals and budget, so you don't have to learn Ads Manager.</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-900/50 backdrop-blur-sm border border-blue-500/20 rounded-lg p-6">
                <h3 className="text-xl font-bold text-white mb-4">Simple reporting</h3>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <span>Monthly summary of reach, engagement, and bookings from social.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <span>No fluff metrics—just what actually matters to your bottom line.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <span>Adjustments based on what's working and what's not.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* How working together works */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-8 mb-12">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">How working together works</h2>
            <div className="space-y-6 max-w-2xl mx-auto">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-lg">
                  1
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Quick audit</h3>
                  <p className="text-gray-300">
                    We look at your current social presence, your target clients, and what's missing. Takes 20 minutes.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-lg">
                  2
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Plan & setup</h3>
                  <p className="text-gray-300">
                    I clean up your profiles, map out a content plan, and get approval before anything goes live.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-lg">
                  3
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Ongoing management</h3>
                  <p className="text-gray-300">
                    I handle the posting, engagement, and adjustments. You just show up to treat patients and watch the bookings roll in.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center bg-blue-500/10 border border-blue-500/30 rounded-lg p-12 mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Ready to get started?</h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Fill out the Start Here form and we'll discuss how this can work for your clinic.
            </p>
            <button
              onClick={() => navigateToStartHere(navigate, location.pathname)}
              className="inline-flex items-center gap-2 px-8 py-4 bg-blue-500 text-white text-lg font-semibold rounded-md hover:bg-blue-600 transition-colors"
            >
              Start Here
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
